import React, { useEffect, useState } from "react";
import { Trophy, Medal, Star, Calendar, Users } from "lucide-react";
import { api } from "../lib/api";

export const CONTEST_STATUS = {
  en_cours: { label: "En cours", cls: "text-neon-green border-neon-green" },
  a_venir: { label: "À venir", cls: "text-blue-400 border-blue-400" },
  termine: { label: "Terminé", cls: "text-zinc-500 border-zinc-600" },
};
const fmtDate = (d) => { try { const [y, m, dd] = d.split("-"); return `${dd}/${m}/${y}`; } catch { return d; } };
export const fmtScore = (metric, s) => {
  if (!metric || metric === "ca") return `${Number(s).toFixed(2)}€ prix usine`;
  const unit = { orders: "commande(s)", new_clients: "nouveau(x) client(s)", product_qty: "unité(s) vendue(s)" }[metric] || "";
  return `${s} ${unit}`;
};

export const RewardsVendor = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/vendor/rewards").then((r) => setData(r.data)).catch(console.error); }, []);
  if (!data) return <p className="text-zinc-500 py-10 text-center">Chargement...</p>;

  const ws = data.week_state;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div data-testid="vendor-tier-card" className="bg-ink-surface border border-zinc-800 p-4">
          <p className="text-zinc-500 text-xs uppercase mb-2">Ta rémunération</p>
          <span data-testid="vendor-tier-badge" className="inline-flex items-center gap-2 border px-3 py-1.5 font-anton uppercase text-xl text-neon-green border-neon-green bg-neon-green/10">
            <Star className="h-5 w-5" /> 50 % du HT
          </span>
          <p className="text-zinc-600 text-xs mt-2">Rémunération unique pour tous les vendeurs : tu gardes la moitié du prix HT de chaque produit vendu + 100 % des frais de livraison HT.</p>
        </div>
        <div data-testid="vendor-points-card" className="bg-ink-surface border border-zinc-800 p-4">
          <p className="text-zinc-500 text-xs uppercase mb-2">Points récompenses {data.year}</p>
          <p data-testid="vendor-points" className="font-anton text-3xl text-neon-green">{data.points} pts</p>
          <p className="text-zinc-500 text-xs mt-1">Gagnés : {data.points_earned} · Échangés : {data.points_spent}</p>
          <p className="text-zinc-600 text-xs mt-1">1€ de ventes prix usine (commandes livrées) = 1 point. Remis à zéro le 1er janvier. Le parrainage ne compte pas.</p>
        </div>
        <div data-testid="vendor-week-card" className="bg-ink-surface border border-zinc-800 p-4">
          <p className="text-zinc-500 text-xs uppercase mb-2">Semaine en cours</p>
          <p data-testid="vendor-week-ca" className="font-anton text-3xl text-white">{ws.current_week_ca.toFixed(2)}€</p>
          <p className="text-zinc-500 text-xs mt-1">de ventes prix usine livrées cette semaine (= points en cours)</p>
        </div>
      </div>

      {data.redemptions.length > 0 && (
        <div>
          <h2 className="font-anton text-2xl uppercase text-white mb-3">Mes cadeaux échangés</h2>
          <div className="bg-ink-surface border border-zinc-800">
            {data.redemptions.map((r) => (
              <div key={r.id} data-testid="vendor-redemption-row" className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/60 text-sm">
                <span className="text-white">🎁 {r.gift}</span>
                <span className="text-zinc-500 text-xs">{fmtDate(r.created_at.slice(0, 10))}</span>
                <span className="text-neon-magenta font-bold">-{r.points} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-anton text-2xl uppercase text-white mb-3 flex items-center gap-2"><Trophy className="h-6 w-6 text-neon-magenta" /> Jeux concours</h2>
        {data.contests.length === 0 ? (
          <p className="text-zinc-500 py-6 text-center border border-dashed border-zinc-800">Aucun jeu concours pour le moment. Reste connecté !</p>
        ) : (
          <div className="space-y-4">
            {data.contests.map((c) => {
              const st = CONTEST_STATUS[c.status] || CONTEST_STATUS.termine;
              return (
                <div key={c.id} data-testid="rewards-contest-card" className="bg-ink-surface border border-zinc-800 p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="font-anton text-xl text-white uppercase">{c.name}</span>
                    <span className={`text-xs uppercase border px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                    <span className="text-xs uppercase border border-neon-magenta/50 text-neon-magenta px-2 py-0.5">{c.metric_label}{c.product_name ? ` : ${c.product_name}` : ""}</span>
                    <span className="text-zinc-500 text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>
                    <span className="text-zinc-500 text-xs flex items-center gap-1"><Users className="h-3 w-3" /> {c.participants} participant{c.participants > 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {c.prizes.map((p, i) => (
                      <span key={`${i}-${p}`} className={`text-xs border px-2 py-1 flex items-center gap-1 ${i === 0 ? "text-yellow-400 border-yellow-400/50" : i === 1 ? "text-zinc-300 border-zinc-500" : "text-amber-600 border-amber-700"}`}>
                        <Medal className="h-3 w-3" /> {i + 1}er : {p}
                      </span>
                    ))}
                  </div>
                  <div data-testid="contest-my-rank" className={`px-3 py-2 mb-3 border text-sm ${c.my_rank ? "border-neon-green/50 bg-neon-green/10 text-neon-green" : "border-zinc-700 text-zinc-400"}`}>
                    {c.my_rank
                      ? <>Ta place en direct : <span className="font-anton text-lg">#{c.my_rank}</span> — {fmtScore(c.metric, c.my_score)}{c.my_prize && <> · cadeau en jeu : <span className="font-bold">{c.my_prize}</span> 🎁</>}</>
                      : "Pas encore classé — livre des commandes pendant le jeu pour entrer au classement !"}
                  </div>
                  {c.leaderboard.length > 0 && (
                    <table className="w-full text-sm">
                      <thead><tr className="text-zinc-600 text-xs uppercase text-left"><th className="py-1">#</th><th>Vendeur</th><th>Ville</th><th className="text-right">Score</th><th className="text-right">Cadeau</th></tr></thead>
                      <tbody>
                        {c.leaderboard.map((r) => (
                          <tr key={r.rank} className={`border-t border-zinc-800 ${r.is_me ? "text-neon-green font-bold" : "text-zinc-300"}`}>
                            <td className="py-1.5">{r.rank}</td>
                            <td>{r.vendor_name}{r.is_me && " (toi)"}</td>
                            <td className="text-zinc-500">{r.city}</td>
                            <td className="text-right">{fmtScore(c.metric, r.score)}</td>
                            <td className="text-right text-neon-magenta">{r.prize || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
